import React from 'react';
import './Charts.css';

export interface DonutSlice {
  value: number;
  color: string;
  label: string;
}

interface DonutChartProps {
  slices: DonutSlice[];
  size?: number;
  label?: string;
  subLabel?: string;
}

export function DonutChart({ slices, size = 180, label, subLabel }: DonutChartProps) {
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (total === 0) return <div className="chart-empty">No data</div>;

  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.35;
  const strokeWidth = size * 0.14;
  const circumference = 2 * Math.PI * r;

  let accumulated = 0;
  const arcs = slices.filter(s => s.value > 0).map((slice) => {
    const fraction = slice.value / total;
    const dashArray = `${circumference * fraction} ${circumference * (1 - fraction)}`;
    const dashOffset = -circumference * accumulated;
    accumulated += fraction;
    return { ...slice, fraction, dashArray, dashOffset };
  });

  return (
    <div className="donut-chart-container">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill="none"
          stroke="var(--border-primary)" strokeWidth={strokeWidth} opacity="0.3" />
        {arcs.map((arc, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={arc.color} strokeWidth={strokeWidth}
            strokeDasharray={arc.dashArray} strokeDashoffset={arc.dashOffset}
            strokeLinecap="butt"
            style={{ transform: 'rotate(-90deg)', transformOrigin: `${cx}px ${cy}px`, transition: 'stroke-dasharray 0.6s ease' }} />
        ))}
        {label && (
          <>
            <text x={cx} y={subLabel ? cy - 4 : cy + 5} textAnchor="middle" className="donut-center-value">{label}</text>
            {subLabel && <text x={cx} y={cy + 14} textAnchor="middle" className="donut-center-label">{subLabel}</text>}
          </>
        )}
      </svg>
      <div className="donut-legend">
        {arcs.map((arc, i) => (
          <div key={i} className="donut-legend-item">
            <span className="legend-dot" style={{ background: arc.color }} />
            <span className="legend-label">{arc.label}</span>
            <span className="legend-value">{arc.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
